import React, { useEffect } from 'react'

import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { CheckboxField } from 'routes/Checkout/Components/AboutYou/CheckboxField'
import { EmailField } from 'routes/Checkout/Components/AboutYou/EmailField'
import { PasswordField } from 'routes/Checkout/Components/AboutYou/PasswordField'
import { CheckoutButton } from 'routes/Checkout/Components/CheckoutButton'
import { checkoutClearErrors, clearPayPalErrors } from 'routes/Checkout/checkoutActions'
import { emailValidator } from 'routes/Checkout/checkoutFormUtils'
import {
  getIsEmailMarketingEnabled,
  getEmail,
  getPasswordErrors,
  getPasswordValue,
} from 'routes/Checkout/checkoutSelectors'
import { useIsApplePayEnabled } from 'routes/Checkout/hooks/useIsApplePayEnabled'
import { isSubmitting as getIsSubmitting } from 'routes/Checkout/utils/state'

type EmailPasswordFormProps = {
  onSubmit: (
    email: string,
    password: string,
    areEmailsAllowed: boolean,
    isApplePay?: boolean,
  ) => void
}

export const EmailPasswordForm = ({ onSubmit }: EmailPasswordFormProps) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(checkoutClearErrors())
    dispatch(clearPayPalErrors())
    dispatch({ type: actionTypes.APPLE_PAY_ERRORS_CLEAR })
  }, [dispatch])

  const isApplePayEnabled = useIsApplePayEnabled()

  const initialEmailValue = useSelector(getEmail)
  const initialPasswordValue = useSelector(getPasswordValue)
  const initialMarketingCheckboxValue = useSelector(getIsEmailMarketingEnabled)
  const isSubmitting = useSelector(getIsSubmitting)
  const passwordErrors = useSelector(getPasswordErrors)

  const { values, touched, errors, setFieldValue, handleChange, handleBlur } = useFormik({
    initialValues: {
      email: initialEmailValue,
      password: initialPasswordValue,
      isEmailMarketingEnabled: initialMarketingCheckboxValue,
    },
    validateOnBlur: true,
    validate: (formValues) => ({
      email: emailValidator(formValues.email),
    }),
    onSubmit: () => {},
  })

  const isPasswordValid = values.password.length > 0 && passwordErrors.length === 0
  const isCTADisabled = values.password.length === 0 || !!errors.email || !isPasswordValid

  return (
    <form autoComplete="off">
      <EmailField
        id="email"
        isTouched={touched.email}
        value={values.email}
        handleChange={handleChange}
        handleBlur={handleBlur}
        error={errors.email}
      />

      <PasswordField
        id="password"
        isTouched={touched.password}
        value={values.password}
        handleChange={handleChange}
        handleBlur={handleBlur}
      />

      <CheckboxField
        id="isEmailMarketingEnabled"
        checked={values.isEmailMarketingEnabled}
        onChange={() => setFieldValue('isEmailMarketingEnabled', !values.isEmailMarketingEnabled)}
      />

      <CheckoutButton
        onClick={() => onSubmit(values.email, values.password, values.isEmailMarketingEnabled)}
        isLoading={isSubmitting}
        isDisabled={isCTADisabled}
      >
        {isApplePayEnabled ? 'Checkout' : 'Continue to Delivery'}
      </CheckoutButton>
    </form>
  )
}
